// src/composables/useDynamicBackground.js
import { onMounted, onBeforeUnmount, ref } from "vue";

/**
 * Composable to handle dynamic background color transitions based on scroll position
 * Uses IntersectionObserver to detect when specific film sections are visible and changes
 * the background color accordingly
 */
export default function useDynamicBackground() {
  const currentVisibleSection = ref(null);
  const currentBodyClasses = ref([]);
  let observer = null;
  let mutationObserver = null; // Reference for MutationObserver
  let lastScrollY = window.scrollY; // Track scroll position for direction detection

  // Theme group is now retrieved directly from section element

  // Function to update the body classes based on section number and theme group
  const updateBodyClasses = (sectionNumber, themeGroup) => {
    const body = document.body;

    // Force parse to integer to ensure proper comparison
    const sectionNum = parseInt(sectionNumber, 10);
    let shouldBeLightTheme = false;

    // Theme group is now passed as parameter

    // Determine theme based on group and section number
    if (themeGroup === "cecilia") {
      shouldBeLightTheme = [2, 4].includes(sectionNum);
    } else if (themeGroup === "artefacts") {
      shouldBeLightTheme = sectionNum === 1;
    } else if (themeGroup === "patricia") {
      // Patricia: sections 2 and 4 should be light, 1 and 3 should be dark
      shouldBeLightTheme = [2, 4].includes(sectionNum);
    } else if (themeGroup === "thepath") {
      // The Path: mirror Patricia's pattern for now (2 and 4 light)
      // Episodes across seasons will also force light (handled below)
      shouldBeLightTheme = [2, 4].includes(sectionNum);
    } else if (themeGroup === "thepath-episodes") {
      // The Path EPISODES with alchemical phases
      // Nigredo (eps 1-3): DARK
      // Albedo (eps 4-5): LIGHT
      // Citrinitas (eps 6-7): LIGHT
      // Rubedo (ep 8): DARK
      shouldBeLightTheme = [4, 5, 6, 7].includes(sectionNum);
    }

    let filmArtefactName;
    if (themeGroup === "cecilia") {
      if (sectionNum === 2) {
        filmArtefactName = "Dreamscapes of No Tomorrow";
      } else if (sectionNum === 4) {
        filmArtefactName = "La Petite Mort";
      } else {
        filmArtefactName = "Cecilia other";
      }
    } else if (themeGroup === "artefacts") {
      filmArtefactName = "Artefacts section";
    } else {
      filmArtefactName = "Unknown";
    }

    // Try to find the drawer component using multiple selectors to ensure we get it
    // The drawer might have different classes in different views
    let drawer = document.querySelector(".BottomDrawer");
    if (!drawer) {
      drawer = document.querySelector("[class*='drawer']");
    }
    if (!drawer) {
      drawer = document.querySelector("[class*='Drawer']");
    }

    // Log drawer information for debugging
    if (drawer) {
    }

    // Apply theme changes
    if (shouldBeLightTheme) {

      if (!body.classList.contains("light-theme")) {
        body.classList.add("light-theme");
      }

      // If drawer exists, make sure it gets the light theme too
      if (drawer && !drawer.classList.contains("light-theme")) {
        drawer.classList.add("light-theme");
      }
    } else {

      if (body.classList.contains("light-theme")) {
        body.classList.remove("light-theme");
      }

      // If drawer exists, remove light theme from it too
      if (drawer && drawer.classList.contains("light-theme")) {
        drawer.classList.remove("light-theme");
      }
    }

    // Dispatch a custom event to notify all components about the theme change
    // This is important for components that might be rendered after our observer is set up
    const themeChangeEvent = new CustomEvent("themeChange", {
      detail: { isLightTheme: shouldBeLightTheme, section: sectionNum },
    });
    window.dispatchEvent(themeChangeEvent);

    // Log all body classes for debugging
    if (drawer) {
    }

    // Update reactive refs for tracking
    currentVisibleSection.value = sectionNumber;
    currentBodyClasses.value = [...body.classList];
  };

  onMounted(() => {

    // Set initial dark theme
    document.body.classList.remove("light-theme");

    const drawer = document.querySelector(".BottomDrawer");
    if (drawer) {
      drawer.classList.remove("light-theme");
    }

    // Listen for episodes expansion events
    window.addEventListener("episodesExpanded", (event) => {
      updateBodyClasses(2, event.detail.themeGroup);
    });

    // Drawer theme is now handled by section observers
    // Removed drawerMounted event listener as it's no longer needed

    currentBodyClasses.value = [...document.body.classList];

    // Configure the intersection observer using the same settings from the working version
    const observerOptions = {
      root: null, // Use the viewport as the root
      rootMargin: "0px 0px 0% 0px", // Negative bottom margin to detect earlier when scrolling up
      threshold: 0.05, // Very low threshold for immediate detection
    };

    observer = new IntersectionObserver((entries) => {

      // Detect scroll direction
      const currentScrollY = window.scrollY;
      const scrollDirection = currentScrollY > lastScrollY ? "down" : "up";
      lastScrollY = currentScrollY;

      // Process only the entries that are intersecting
      const visibleEntries = entries.filter((entry) => entry.isIntersecting);

      if (visibleEntries.length === 0) return;

      // Sort by visibility ratio in descending order to prioritize the most visible section
      visibleEntries.sort((a, b) => b.intersectionRatio - a.intersectionRatio);

      // Log intersection ratios for debugging
      visibleEntries.forEach((entry, index) => {
      });

      // Get the most visible entry
      const mostVisibleEntry = visibleEntries[0];

      // Get theme group directly from section element (declare once here)
      const themeGroup = mostVisibleEntry.target.dataset.themeGroup || "";

      // Check if this is an episode section - force light theme for Patricia "Man" season and The Path seasons
      const isEpisode =
        mostVisibleEntry.target.classList.contains("episode-section");

      if (
        isEpisode &&
        (themeGroup === "patricia" || themeGroup === "thepath")
      ) {
        // Force section 2 theme (light) while episodes are active/visible
        updateBodyClasses(2, themeGroup);
        return;
      }

      // Find the section number from the circleNumber element
      let sectionNumber;

      // First try to find it from the previous sibling element
      const filmTitleContainer = mostVisibleEntry.target.previousElementSibling;
      if (filmTitleContainer) {
        const circleNumberElement =
          filmTitleContainer.querySelector(".circleNumber");
        if (circleNumberElement) {
          sectionNumber = circleNumberElement.textContent.trim();
        }
      }

      // If that fails, try looking inside the section itself for a circleNumber
      if (!sectionNumber) {
        const circleNumberElement =
          mostVisibleEntry.target.querySelector(".circleNumber");
        if (circleNumberElement) {
          sectionNumber = circleNumberElement.textContent.trim();
        }
      }

      // If still no section number, return
      if (!sectionNumber) {
        return;
      }

      // Special handling for Story 3 when scrolling down
      if (
        scrollDirection === "down" &&
        sectionNumber === "3" &&
        themeGroup === "patricia"
      ) {
        const rect = mostVisibleEntry.target.getBoundingClientRect();

        // Only trigger background change if Story 3 is very close to the top (within 150px)
        if (rect.top > 150) {
          return; // Don't change background yet
        }
      }

      // Only update if a different section is now most visible
      if (sectionNumber !== currentVisibleSection.value) {
        updateBodyClasses(sectionNumber, themeGroup);
      }
    }, observerOptions);

    // Observe all film sections just like in the working version
    const filmSections = document.querySelectorAll(".story-section");

    filmSections.forEach((section) => {
      observer.observe(section);
    });

    // Watch for new sections added dynamically
    mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            // Check if the node itself is a story-section
            if (node.classList?.contains("story-section")) {
              observer.observe(node);
            }

            // Also check for story-sections within the added node (for Vue transitions)
            const nestedSections = node.querySelectorAll
              ? node.querySelectorAll(".story-section")
              : [];
            nestedSections.forEach((section) => {
              observer.observe(section);
            });
          }
        });
      });
    });

    mutationObserver.observe(document.body, { childList: true, subtree: true });
  });

  onBeforeUnmount(() => {
    // Clean up the observer when the component is destroyed
    if (observer) {
      observer.disconnect();
    }

    // Clean up observers
    if (mutationObserver) {
      mutationObserver.disconnect();
    }

    // Remove our custom event listener
    window.removeEventListener("drawerMounted", null);

    // Reset body classes
    document.body.classList.remove("light-theme");
    const drawer = document.querySelector(".BottomDrawer");
    if (drawer) {
      drawer.classList.remove("light-theme");
    }
  });

  return {
    currentVisibleSection,
    currentBodyClasses,
  };
}
